import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from './components/ui';
import { useAuthStore, useSocketConnection } from '@soundbyte/stores';

// Layouts
import { AuthLayout } from './layouts/AuthLayout';
import { DashboardLayout } from './layouts/DashboardLayout';
import { ScrollToTop } from './components/shared';

// Lazy load pages
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const SignupPage = React.lazy(() => import('./pages/auth/SignupPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage'));
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const SonicWizardPage = React.lazy(() => import('./pages/sonic/SonicWizardPage'));
const SonicCreatingPage = React.lazy(() => import('./pages/sonic/SonicCreatingPage'));
const SonicRevealPage = React.lazy(() => import('./pages/sonic/SonicRevealPage'));
const SonicPrintPage = React.lazy(() => import('./pages/sonic/SonicPrintPage'));
const SonicSuccessPage = React.lazy(() => import('./pages/sonic/SonicSuccessPage'));
const OriginalsWizardPage = React.lazy(() => import('./pages/originals/OriginalsWizardPage'));
const OriginalsDetailPage = React.lazy(() => import('./pages/originals/OriginalsDetailPage'));
const OriginalsPaymentSuccessPage = React.lazy(() => import('./pages/originals/OriginalsPaymentSuccessPage'));
const ShopPage = React.lazy(() => import('./pages/shop/ShopPage'));
const CollectionDetailPage = React.lazy(() => import('./pages/collection/CollectionDetailPage'));
const CollectionOrderSuccessPage = React.lazy(() => import('./pages/collection/CollectionOrderSuccessPage'));
const CollectionOrderDetailPage = React.lazy(() => import('./pages/collection/CollectionOrderDetailPage'));
const GalleryPage = React.lazy(() => import('./pages/dashboard/GalleryPage'));
const SettingsPage = React.lazy(() => import('./pages/dashboard/SettingsPage'));
const SubscriptionPage = React.lazy(() => import('./pages/dashboard/SubscriptionPage'));
const OrdersPage = React.lazy(() => import('./pages/dashboard/OrdersPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

const queryClient = new QueryClient();

// Loading fallback
const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const App = () => {
  const initialize = useAuthStore((state) => state.initialize);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Connect to WebSocket when authenticated
  useSocketConnection(isAuthenticated);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <React.Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Redirect root to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Auth Routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
            </Route>

            {/* Stripe Checkout Success (outside DashboardLayout for session restore) */}
            <Route path="/sonic/success" element={<SonicSuccessPage />} />
            <Route path="/originals/:id/payment-success" element={<OriginalsPaymentSuccessPage />} />
            <Route path="/collection/order-success" element={<CollectionOrderSuccessPage />} />

            {/* Dashboard Routes (Protected) */}
            <Route element={<DashboardLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              {/* Legacy alias — redirect to the canonical /orders so there's one source of truth. */}
              <Route path="/dashboard/orders" element={<Navigate to="/orders" replace />} />
              <Route path="/create" element={<Navigate to="/sonic" replace />} />
              {/* Sonic Identity - Phase 1 (Digital) */}
              <Route path="/sonic" element={<SonicWizardPage />} />
              <Route path="/sonic/creating/:id" element={<SonicCreatingPage />} />
              <Route path="/sonic/reveal/:id" element={<SonicRevealPage />} />
              {/* Sonic Identity - Phase 2 (Physical) */}
              <Route path="/sonic/print/:id" element={<SonicPrintPage />} />
              <Route path="/originals" element={<OriginalsWizardPage />} />
              <Route path="/originals/:id" element={<OriginalsDetailPage />} />
              {/* Art Shop */}
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/collection/:slug" element={<CollectionDetailPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/sonic" element={<OrdersPage />} />
              <Route path="/orders/originals" element={<OrdersPage />} />
              <Route path="/orders/collection" element={<OrdersPage />} />
              <Route path="/orders/collection/:orderId" element={<CollectionOrderDetailPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/subscription" element={<SubscriptionPage />} />
            </Route>

            {/* Legal */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </React.Suspense>
        <ToastContainer />
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
